/**
 * Portal Entry Point for Communication Domain
 *
 * This file registers all pages and custom CRUD components with the portal system.
 * It is imported by the portal's virtual:portal-entries module.
 *
 * Custom Components (SSOT):
 * - Communication::RecipientPicker - Block for selecting recipients/responders (supports modes)
 * - Communication::AnalyticsPanel - Tab for viewing delivery analytics
 * - Communication::ScheduleManager - Block for scheduling communications
 */

import { defineAsyncComponent } from "vue";

import {
    registerBlockComponent,
    registerTabComponent,
} from "@ja/crud/services/componentRegistry";
import {
    registerNotificationItemRenderer,
} from "@ja/notifications/notificationItemRenderers";
import type { NotificationData } from "@ja/notifications/types/notification";
import { registerPage } from "@ja/portal/pageRegistry";

import { useMessenger } from "./composables/useMessenger";

// Register pages with the portal
registerPage(
    "communication",
    "Base/Inbox",
    () => import("./Pages/Inbox.vue")
);

registerPage(
    "communication",
    "Base/CommunicationDetail",
    () => import("./Pages/CommunicationDetail.vue")
);

registerPage(
    "communication",
    "Base/ConversationThread",
    () => import("./Pages/ConversationThread.vue")
);

registerPage(
    "communication",
    "Base/CommunicationAnalytics",
    () => import("./Pages/CommunicationAnalytics.vue")
);

registerPage(
    "communication",
    "Base/TemplateManager",
    () => import("./Pages/TemplateManager.vue")
);

// ============================================================================
// Custom CRUD Components (SSOT Registration)
// ============================================================================
// These components are used via customBlock() and customTab() in CRUD configs.
// Naming convention: {Domain}::{ComponentName}

/**
 * RecipientPicker - Block for selecting recipients with target type selector.
 *
 * @example
 * ```php
 * ->customBlock(
 *   key: 'recipient_targeting',
 *   component: 'Communication::RecipientPicker',
 *   componentProps: ['availableTypes' => ['by_roles', 'all_users']]
 * )
 * ```
 */
registerBlockComponent(
    "Communication::RecipientPicker",
    () => import("./Components/RecipientPicker/RecipientPicker.vue"),
    "communication"
);

/**
 * ResponderPicker - Block for selecting responders with permissions.
 *
 * @example
 * ```php
 * ->customBlock(
 *   key: 'responder_targeting',
 *   component: 'Communication::ResponderPicker',
 *   componentProps: ['fieldKey' => 'responder_targeting']
 * )
 * ```
 */
registerBlockComponent(
    "Communication::ResponderPicker",
    () => import("./Components/ResponderPicker/ResponderPicker.vue"),
    "communication"
);

/**
 * AnalyticsPanel - Custom tab for viewing communication delivery analytics.
 *
 * Usage in CRUD config:
 * ```php
 * ->customTab(
 *   key: 'analytics',
 *   component: 'Communication::AnalyticsPanel',
 *   icon: 'bar-chart-2'
 * )
 * ```
 */
registerTabComponent(
    "Communication::AnalyticsPanel",
    () => import("./Components/AnalyticsPanel/AnalyticsPanel.vue"),
    "communication"
);

/**
 * UserCommunicationsTab - User CRUD tab: inbox rows + remove unacknowledged recipients.
 */
registerTabComponent(
    "Communication::UserCommunicationsTab",
    () => import("./Components/UserCommunicationsTab.vue"),
    "communication"
);

/**
 * ScheduleManager - Custom block for scheduling communications.
 *
 * Usage in CRUD config:
 * ```php
 * ->customBlock(
 *   key: 'schedule_manager',
 *   component: 'Communication::ScheduleManager'
 * )
 * ```
 */
registerBlockComponent(
    "Communication::ScheduleManager",
    () => import("./Components/ScheduleManager/ScheduleManagerBlock.vue"),
    "communication"
);

/**
 * ScheduleHistory - Custom block for displaying schedule history.
 *
 * Usage in CRUD config:
 * ```php
 * ->customBlock(
 *   key: 'schedule_history',
 *   component: 'Communication::ScheduleHistory'
 * )
 * ```
 */
registerBlockComponent(
    "Communication::ScheduleHistory",
    () => import("./Components/ScheduleManager/ScheduleHistoryBlock.vue"),
    "communication"
);

/**
 * AnnouncementDetailsBlock - Read-only viewer block for Announcements.
 *
 * Renders header metadata (type, priority, status, sender, channels,
 * expires_at), engagement requirements, the markdown body and the
 * required-trainings list.
 *
 * Usage in CRUD config:
 * ```php
 * ->customBlock(
 *   key: 'announcement_details',
 *   component: 'Communication::AnnouncementDetailsBlock',
 *   dataFields: ['title', 'content', 'priority', 'status', ...]
 * )
 * ```
 */
registerBlockComponent(
    "Communication::AnnouncementDetailsBlock",
    () => import("./Components/AnnouncementDetails/AnnouncementDetailsBlock.vue"),
    "communication"
);

// ============================================================================
// Notification renderer (cross-domain integration)
// ============================================================================
// The Notifications domain renders rows generically by default. Communication
// owns the announcement type, so it registers a renderer that:
//   1. Detects announcement-shaped notifications via metadata + action_url.
//   2. Replaces the row with `AnnouncementNotificationRow` (announcement card).
//   3. Opens the announcement in the messenger dialog instead of navigating.
//
// Notifications has zero compile-time knowledge of Communication; the registry
// is the only contact point.
function isAnnouncementNotification(n: NotificationData): boolean {
    const metadata = (n.metadata ?? {}) as Record<string, unknown>;
    if (typeof metadata.announcement_id === "string") return true;
    if (metadata.type_key === "communication.new_announcement") return true;
    const looksLikeAnnouncementRoute =
        typeof n.action_url === "string" && /\/inbox\/[0-9a-f-]+$/i.test(n.action_url);
    return looksLikeAnnouncementRoute && n.icon === "megaphone";
}

function extractAnnouncementId(n: NotificationData): string | null {
    const metadata = (n.metadata ?? {}) as Record<string, unknown>;
    const id = metadata.announcement_id;
    if (typeof id === "string" && id !== "") return id;
    if (typeof n.action_url === "string") {
        const match = n.action_url.match(/\/inbox\/([0-9a-f-]+)$/i);
        if (match?.[1]) return match[1];
    }
    return null;
}

registerNotificationItemRenderer({
    priority: 10,
    matches: isAnnouncementNotification,
    component: defineAsyncComponent(
        () => import("./Components/AnnouncementDisplay/AnnouncementNotificationRow.vue"),
    ),
    onClick(notification) {
        const id = extractAnnouncementId(notification);
        if (id === null) return false;
        void useMessenger().viewCommunication(id);
        return true;
    },
});

